import {
  Component,
  computed,
  effect,
  EnvironmentInjector,
  inject,
  runInInjectionContext,
  signal,
  ViewChild,
} from '@angular/core';
import { WizardStepDto, WizardStepStage } from '../../models/client-models/entities/wizard-step-dto';
import { CompletenessStatus } from '../../models/client-models/enums/completeness-status';
import { PaperSideType } from '../../models/server-models/enums/paper-side-type';
import { toSignal } from '@angular/core/rxjs-interop';
import { PrintOfficeService } from '../../services/print-office.service';
import _, { chunk } from 'lodash';
import { ExpandedPrintVariation } from '../../models/client-models/entities/expanded-print-variation';
import { ExpandedPaperSize } from '../../models/client-models/entities/expanded-paper-size';
import { ExpandedPaperType } from '../../models/client-models/entities/expanded-paper-type';
import { ExpandedPaperExtra } from '../../models/client-models/entities/expanded-paper-extra';
import { PrintVariationService } from '../../services/print-variation.service';
import { FilesSelectionComponent } from '../wizard/files-selection/files-selection.component';
import { UploadedFile } from '../../models/server-models/entities/uploaded-file';
import { PaperColorType } from '../../models/server-models/enums/paper-color-type';
import { ErrorService } from '../../../../shared/services/error.service';
import { WizardStepService } from '../../services/wizard-step.service';
import { PrintOfficeWithOfferDto } from '../wizard/print-office-selection/print-office-selection.component';
import { ToastService } from '../../services/toast.service';
import { AuthService } from '../../services/auth/auth.service';
import { Router } from '@angular/router';
import { PrintOrderService } from '../../services/print-order.service';
import { onAuthStateChanged } from '@angular/fire/auth';
import { AuthApiService } from '../../services/auth-api.service';
import { GeneralEventsService } from '../../services/in-app-services/general-events.service';
import { Subscription } from 'rxjs';
import { LoggerService } from '../../../../shared/services/logger.service';
import { ScrollDetail } from '@ionic/angular';
import { IonContent } from '@ionic/angular';

@Component({
  selector: 'app-home',
  templateUrl: 'home.page.html',
  styleUrls: ['home.page.scss'],
  standalone: false,
})
export class HomePage {
  printOfficeService = inject(PrintOfficeService);
  printVariationService = inject(PrintVariationService);
  errorService = inject(ErrorService);
  wizardStepService = inject(WizardStepService);
  toastService = inject(ToastService);
  authService = inject(AuthService);
  authApiService = inject(AuthApiService);
  router = inject(Router);
  injector = inject(EnvironmentInjector);
  generalEventsService = inject(GeneralEventsService);
  loggerService = inject(LoggerService);

  printOrderService = inject(PrintOrderService);

  showScrollIndicator = true;
  checkIfScrollIndicatorNeededChecked = false;

  @ViewChild('filesSelectionComponent') filesSelectionComponent?: FilesSelectionComponent;
  @ViewChild(IonContent, { static: false }) ionContent!: IonContent;

  completenessStatus = CompletenessStatus;
  stages = WizardStepStage;

  printOffices = toSignal(this.printOfficeService.getPrintOffices(), {
    initialValue: [],
  });

  printVariations = computed(() => {
    return this.printVariationService.getPrintVariations(this.printOffices());
  });

  paperSizes = computed(() => {
    return this.selectedVariation()?.paperSizes ?? [];
  });

  paperTypes = computed(() => {
    return this.selectedPaperSize()?.paperTypes ?? [];
  });

  paperExtras = computed(() => {
    return this.selectedPaperType()?.paperExtras ?? [];
  });

  wizardSteps = signal<WizardStepDto[]>(this.wizardStepService.getWizardSteps());

  chunkedSteps = computed(() => {
    return chunk(this.wizardSteps(), 4);
  });

  currentStep = computed(() => this.wizardSteps().find((step) => step.status === CompletenessStatus.Active));
  currentStepStage = computed(() => this.currentStep()!.stage);

  selectedVariation = signal<ExpandedPrintVariation | null>(null);
  selectedPaperSize = signal<ExpandedPaperSize | null>(null);
  selectedPaperType = signal<ExpandedPaperType | null>(null);
  selectedPaperSideType = signal<PaperSideType | null>(null);
  selectedPaperColorType = signal<PaperColorType | null>(null);
  selectedExtras = signal<ExpandedPaperExtra[]>([]);
  uploadedFiles = signal<UploadedFile[]>([]);
  selectedOffice = signal<PrintOfficeWithOfferDto | null>(null);

  private homePageResetSubscription: Subscription;

  constructor() {
    effect(() => this.setOnStepOpened());

    this.resetStateOnUserChange();

    this.homePageResetSubscription = this.generalEventsService.onHomePageResetEvent().subscribe(() => {
      this.resetComponentState();
    });
  }

  ngOnDestroy() {
    this.homePageResetSubscription?.unsubscribe();
  }

  private async checkIfScrollIndicatorNeeded() {
    try {
      if (this.currentStepStage() != WizardStepStage.Payment) return;

      if (this.checkIfScrollIndicatorNeededChecked) return;

      const scrollEl = await this.ionContent.getScrollElement();
      this.showScrollIndicator = scrollEl.scrollHeight > scrollEl.clientHeight;

      this.checkIfScrollIndicatorNeededChecked = true;
    } catch (err) {
      this.loggerService.logException(err, 'Failed to get scroll element:');
      this.showScrollIndicator = false;
    }
  }

  onVariationChange(variation: ExpandedPrintVariation | null) {
    this.selectedPaperSize.set(null);
    this.selectedPaperType.set(null);
    this.selectedPaperSideType.set(null);
    this.selectedPaperColorType.set(null);
    this.selectedExtras.set([]);
    this.filesSelectionComponent?.cleanUpFiles();
    this.selectedOffice.set(null);

    this.updateStepStatusToHasSelection(WizardStepStage.VariationSelection);
    this.resetStepsAfterStage(WizardStepStage.VariationSelection);
  }

  onPaperSizeChange(paperSize: ExpandedPaperSize | null) {
    this.selectedPaperType.set(null);
    this.selectedPaperSideType.set(null);
    this.selectedPaperColorType.set(null);
    this.selectedExtras.set([]);
    this.filesSelectionComponent?.cleanUpFiles();
    this.selectedOffice.set(null);

    this.updateStepStatusToHasSelection(WizardStepStage.PaperSizeSelection);
    this.resetStepsAfterStage(WizardStepStage.PaperSizeSelection);
  }

  onPaperTypeChange(paperType: ExpandedPaperType | null) {
    this.selectedPaperSideType.set(null);
    this.selectedPaperColorType.set(null);
    this.selectedExtras.set([]);
    this.filesSelectionComponent?.cleanUpFiles();
    this.selectedOffice.set(null);

    this.updateStepStatusToHasSelection(WizardStepStage.PaperTypeSelection);
    this.resetStepsAfterStage(WizardStepStage.PaperTypeSelection);
  }

  onPaperSideTypeChange(side: PaperSideType | null) {
    if (this.selectedPaperSideType() != null && this.selectedPaperColorType() != null) {
      this.updateStepStatusToHasSelection(WizardStepStage.PrintStyleSelection);
    }

    this.filesSelectionComponent?.cleanUpFiles();
    this.selectedOffice.set(null);
  }

  onPaperColorTypeChange(colorType: PaperColorType | null) {
    if (this.selectedPaperSideType() != null && this.selectedPaperColorType() != null) {
      this.updateStepStatusToHasSelection(WizardStepStage.PrintStyleSelection);
    }

    this.filesSelectionComponent?.cleanUpFiles();
    this.selectedOffice.set(null);
  }

  onExtrasChange(extras: ExpandedPaperExtra[]) {
    this.selectedExtras.set(extras);
    this.filesSelectionComponent?.cleanUpFiles();
    this.selectedOffice.set(null);
  }

  onSelectedOfficeChange(office: PrintOfficeWithOfferDto | null) {
    this.updateStepStatusToHasSelection(WizardStepStage.PrintOfficeSelection);
  }

  async onNextClick() {
    if (!this.authService.isAuthenticated()) {
      this.router.navigateByUrl('/login');
      return;
    }
    this.moveToStep(true);
  }

  onBackClick() {
    this.moveToStep(false);
  }

  private async moveToStep(forward: boolean) {
    const currentIndex = this.wizardSteps().findIndex((step) => step.status === CompletenessStatus.Active);
    var currentStep = this.wizardSteps()[currentIndex];
    let nextStepIndex = currentIndex + (forward ? 1 : -1);

    if (nextStepIndex >= 0 && nextStepIndex < this.wizardSteps().length) {
      const step = this.wizardSteps()[nextStepIndex];

      if (currentStep.stage == WizardStepStage.FilesSelection && forward) {
        let uploadedFiles = await this.filesSelectionComponent!.uploadFiles();

        if (uploadedFiles.length == 0) return;

        this.uploadedFiles.set(uploadedFiles);

        this.updateStepStatusToHasSelection(WizardStepStage.FilesSelection);
      }

      this.goTo(step, forward);
      step.onStepOpened?.(forward);
    }
  }

  private goTo(toStep: WizardStepDto, forward: boolean) {
    if (forward && !this.hasCurrentStepSelection()) return;

    this.loggerService.logEvent('go_to_step', {
      fromStep: this.currentStep()?.stage,
      toStep: toStep.stage,
      forward,
      paperType: this.selectedPaperType()?.name.en ?? 'none',
      paperSideType: this.selectedPaperSideType() ?? 'none',
      paperColorType: this.selectedPaperColorType() ?? 'none',
      extras: this.selectedExtras()
        .map((extra) => extra.name.en)
        .join(','),
      paperSize: this.selectedPaperSize()?.name.en ?? 'none',
      variation: this.selectedVariation()?.name.en ?? 'none',
      office: this.selectedOffice()?.name.en ?? 'none',
    });

    const updatedSteps = this.wizardSteps().map((step) => {
      let status: CompletenessStatus;

      if (step === toStep) {
        status = CompletenessStatus.Active;
      } else if (step.hasOptionSelected) {
        status = CompletenessStatus.Completed;
      } else {
        status = CompletenessStatus.None;
      }

      return {
        ...step,
        status,
      };
    });

    this.wizardSteps.set(updatedSteps);
  }

  resetStepsAfterStage(stage: WizardStepStage) {
    const stepIndex = this.wizardSteps().findIndex((s) => s.stage === stage);

    const updatedSteps = this.wizardSteps().map((step, i) =>
      i > stepIndex ? { ...step, status: CompletenessStatus.None, hasOptionSelected: false } : step
    );

    this.wizardSteps.set(updatedSteps);
  }

  updateStepStatusToHasSelection(stage: WizardStepStage) {
    this.wizardSteps.update((steps) => {
      const step = steps.find((s) => s.stage === stage);
      if (step) step.hasOptionSelected = true;
      return steps;
    });
  }

  hasCurrentStepSelection(): boolean {
    const currentStep = this.currentStep()!;

    return currentStep.hasOptionSelected || currentStep.isOptional;
  }

  async onPaymentListScroll(event: CustomEvent<ScrollDetail>) {
    if (!this.showScrollIndicator) return;

    if (this.currentStepStage() != WizardStepStage.Payment) return;

    const target = await this.ionContent.getScrollElement();

    const threshold = 130;
    const scrollTop = event.detail.scrollTop;

    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    const atBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    this.showScrollIndicator = !atBottom;
  }

  private setOnStepOpened() {
    const steps = this.wizardSteps();

    const extrasStep = steps.find((step) => step.stage === WizardStepStage.ExtrasSelection)!;
    const paymentStep = steps.find((step) => step.stage === WizardStepStage.Payment)!;

    extrasStep.onStepOpened = (wasNavigationForward: boolean) => {
      this.updateStepStatusToHasSelection(WizardStepStage.ExtrasSelection);

      if (this.paperExtras().length == 0 && wasNavigationForward) {
        this.onNextClick();
      }
    };

    paymentStep.onStepOpened = (wasNavigationForward: boolean) => {
      this.checkIfScrollIndicatorNeeded();
    };
  }

  private resetStateOnUserChange() {
    runInInjectionContext(this.injector, () => {
      onAuthStateChanged(this.authService.auth, (_) => {
        this.resetComponentState();
      });
    });
  }

  private resetComponentState() {
    // Clear signals
    this.selectedVariation.set(null);
    this.selectedPaperSize.set(null);
    this.selectedPaperType.set(null);
    this.selectedPaperSideType.set(null);
    this.selectedPaperColorType.set(null);
    this.selectedExtras.set([]);
    this.uploadedFiles.set([]);
    this.selectedOffice.set(null);

    // Clean up file selection
    this.filesSelectionComponent?.cleanUpFiles();

    // Reset wizard steps
    const resetSteps = this.wizardStepService.getWizardSteps();

    this.wizardSteps.set(resetSteps);

    // Reset any custom logic like extras auto-skip
    this.setOnStepOpened();
  }
}
